import { useCallback, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { API } from '@/utils/API';
import {
  FormFieldValue,
  FormTreeData,
} from '@/features/submission_forms/types';
import { buildQuestionData } from './useFormTree';
import { buildVisibilityMap, isAnsweredValue } from './questionVisibility';

const EMPTY_FORM_TREE: FormTreeData[] = [];

// Upload/file answers are persisted through the upload endpoints; the answers
// endpoint ignores an '' for them, so there is nothing to clear via this path.
const UPLOAD_KINDS = new Set(['upload', 'file']);

const sameValue = (
  a: FormFieldValue | undefined,
  b: FormFieldValue | undefined
): boolean =>
  Array.isArray(a) && Array.isArray(b)
    ? a.length === b.length && a.every((v, i) => v === b[i])
    : a === b;

interface UseSubmissionSaveOptions {
  submissionId: string | undefined;
  form: UseFormReturn;
  formTree?: FormTreeData[];
  savedAnswers: Record<string, FormFieldValue> | undefined;
  onProgressRefetch: () => void | Promise<void>;
}

/**
 * Hook to handle saving submission answers and refreshing progress
 * Coordinates the save operation with backend progress calculations
 */
export const useSubmissionSave = ({
  submissionId,
  form,
  formTree = EMPTY_FORM_TREE,
  savedAnswers,
  onProgressRefetch,
}: UseSubmissionSaveOptions) => {
  const serverErrorSlugsRef = useRef(new Set<string>());

  // Last-known server-side value per slug. `savedAnswers` is only fetched once
  // on mount, so we track what we've successfully POSTed since. Diffing against
  // this (rather than mount-time savedAnswers) means: changed values are sent
  // once, not on every autosave; a value we cleared on the server is re-sent if
  // it becomes visible again; and a now-hidden conditional question with a
  // stale server answer gets cleared.
  const serverValuesRef = useRef<Record<string, FormFieldValue>>({});
  useEffect(() => {
    // Server truth wins for slugs it knows about; keep what we've posted for
    // slugs it doesn't (e.g. answered in-session before a refetch).
    serverValuesRef.current = {
      ...serverValuesRef.current,
      ...(savedAnswers || {}),
    };
  }, [savedAnswers]);

  const handleSave = useCallback(async () => {
    if (!submissionId) {
      throw new Error('No submission ID provided');
    }

    const currentFormValues = form.getValues();
    const serverValues = serverValuesRef.current;

    // Build a TRANSITIVE visibility map: slug → visible. A question whose
    // ancestor's condition is unmet is hidden even if its own parent still
    // holds a stale value. Questions not in the tree are treated as visible
    // (no dependency info → can't determine → safe to save).
    const allQuestions = buildQuestionData(formTree);
    const kindBySlug = new Map(allQuestions.map(q => [q.slug, q.kind]));
    const visibilityBySlug = buildVisibilityMap(
      allQuestions,
      currentFormValues
    );

    const formattedAnswers: {
      question_slug: string;
      answer_value: FormFieldValue;
    }[] = [];

    Object.entries(currentFormValues).forEach(
      ([question_slug, answer_value]) => {
        const visible = visibilityBySlug.get(question_slug) ?? true;

        if (visible) {
          if (!sameValue(serverValues[question_slug], answer_value)) {
            formattedAnswers.push({ question_slug, answer_value });
          }
          return;
        }

        // Hidden conditional field: don't send its (possibly stale) local value,
        // but DO clear it on the server if the server still holds an answer.
        // Otherwise the stale answer keeps its own dependents visible/required
        // and blocks submission on questions the user can no longer see.
        if (UPLOAD_KINDS.has(kindBySlug.get(question_slug) ?? '')) return;
        if (isAnsweredValue(serverValues[question_slug])) {
          formattedAnswers.push({ question_slug, answer_value: '' });
        }
      }
    );
    const changedSlugs = formattedAnswers.map(answer => answer.question_slug);

    // Save to backend
    const response = await API.authenticated.fetch(
      `/submissions/${submissionId}/submission_answers`,
      {
        method: 'POST',
        body: JSON.stringify({ answers: formattedAnswers }),
      }
    );

    if (!response.ok) {
      let message = 'Unable to save your answers. Please try again.';
      try {
        const data = await response.json();
        if (data.field_errors && typeof data.field_errors === 'object') {
          Object.entries(data.field_errors).forEach(([slug, errorMessage]) => {
            serverErrorSlugsRef.current.add(slug);
            form.setError(slug, {
              type: 'server',
              message: String(errorMessage),
            });
          });
        }
        message = data.error || data.message || message;
      } catch {
        // body wasn't JSON — keep the generic message
      }
      throw new Error(message);
    }

    // Remember what the server now holds so hidden-field clears aren't resent
    formattedAnswers.forEach(({ question_slug, answer_value }) => {
      serverValuesRef.current[question_slug] = answer_value;
    });

    const resolvedServerErrorSlugs = changedSlugs.filter(slug =>
      serverErrorSlugsRef.current.has(slug)
    );
    if (resolvedServerErrorSlugs.length > 0) {
      form.clearErrors(resolvedServerErrorSlugs);
      resolvedServerErrorSlugs.forEach(slug =>
        serverErrorSlugsRef.current.delete(slug)
      );
    }

    // Progress refetch is best-effort — its failure must not propagate back through
    // onSave(), which would leave useAutosave's isStale = true permanently and
    // freeze the "Syncing..." indicator indefinitely.
    void onProgressRefetch();
  }, [form, formTree, submissionId, onProgressRefetch]);

  return { handleSave };
};
